import { inject } from '@angular/core';
import {signalStore, withHooks, withState} from '@ngrx/signals';
import {Dispatcher, Events, on, withEventHandlers, withReducer} from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import { mergeMap, switchMap, filter} from 'rxjs';
import {
  AssetData,
  Label,
  Network,
  Period,
  ReviewData,
  ReviewsRequest
} from '@models/online-reputation.models';
import { ReviewsService } from '@core/services/reviews.service';
import { withLogger } from '@core/stores/features/logger.feature';
import {
  createReplyTemplate,
  deleteReplyTemplate,
  generateComment,
  loadLabels,
  loadMoreReviews,
  loadReplyTemplates,
  loadReviews,
  reviewsApiEvents,
  submitReviewReply,
  tagReviewWithLabel,
  updateReplyTemplate,
  clearReviews
  ,
  setReplyFormUi,
  clearReplyFormLocalErrors
} from '@core/events/reviews.events';
import {ApplicationStore} from '@core/stores/application.store';
import {FilterStore} from '@core/stores/filter.store';
import {applicationInitialized} from '@core/events/application.events';
import {applyFilter, clearAllFilters, clearFilter} from '@core/events/filter.events';

export type ReviewsState = {
  reviews: ReviewData[];
  labels: Label[];
  totalElements: number;
  pageToken: string | undefined;
  loading: boolean;
  error: string | undefined;
  templates: AssetData[];
  loadingTemplates: boolean;
  savingTemplate: boolean;
  deletingTemplate: boolean;
  submittingReply: boolean;
  templateError: string | undefined;
  submitError: string | undefined;
  templateMutationVersion: number;
  replySubmitVersion: number;
  lastMutatedTemplateId: string | undefined;
  generatingComment: boolean;
  generateCommentError: string | undefined;
  generatedComment: string | undefined;
  // UI state managed in store to avoid ExpressionChangedAfterItHasBeenChecked errors
  // use string types here to avoid coupling to component-local type aliases
  replyFormActiveTab?: string;
  replyFormTemplateMode?: string;
  replyFormTemplateSuccessMessage?: string;
  replyFormLocalTemplateError?: string | undefined;
  replyFormLocalSubmitError?: string | undefined;
  replyFormLocalGenerateCommentError?: string | undefined;
  loadingTagForm: boolean;
  tagReviewWithLabelError: string | undefined;
  tagReviewWithLabelVersion: number;
};

const initialState: ReviewsState = {
  reviews: [],
  labels: [],
  totalElements: 0,
  pageToken: undefined,
  loading: false,
  error: undefined,
  templates: [],
  loadingTemplates: false,
  savingTemplate: false,
  deletingTemplate: false,
  submittingReply: false,
  templateError: undefined,
  submitError: undefined,
  templateMutationVersion: 0,
  replySubmitVersion: 0,
  lastMutatedTemplateId: undefined,
  generatingComment: false,
  generateCommentError: undefined,
  generatedComment: undefined,
  // UI state defaults
  replyFormActiveTab: 'select',
  replyFormTemplateMode: 'custom',
  replyFormTemplateSuccessMessage: undefined,
  replyFormLocalTemplateError: undefined,
  replyFormLocalSubmitError: undefined,
  replyFormLocalGenerateCommentError: undefined,
  loadingTagForm: false,
  tagReviewWithLabelError: undefined,
  tagReviewWithLabelVersion: 0
};

export const ReviewsStore = signalStore(
  { providedIn: 'root' },
  withState<ReviewsState>(initialState),
  withLogger('ReviewsStore'),
  withReducer(
    on(loadReviews, () => ({
      loading: true,
      error: undefined,
      reviews: [],
      totalElements: 0,
      pageToken: undefined
    })),
    on(loadMoreReviews, () => ({
      loading: true,
      error: undefined
    })),
    on(reviewsApiEvents.loadReviewsSuccess, ({ payload }) => ({
      reviews: payload.data ?? [],
      totalElements: payload.total ?? 0,
      pageToken: payload.nextPageToken,
      loading: false,
      error: undefined
    })),
    on(reviewsApiEvents.loadReviewsFailure, ({ payload: error }) => ({
      reviews: [],
      totalElements: 0,
      pageToken: undefined,
      loading: false,
      error
    })),
    on(reviewsApiEvents.loadMoreReviewsSuccess, ({ payload }, state) => {
      // Merge reviews: override existing reviews with same id from payload.data
      const existingReviewsMap = new Map(state.reviews.map(r => [r.id, r]));
      (payload.data ?? []).forEach(r => {
        existingReviewsMap.set(r.id, r);
      });
      // To preserve order: existing reviews first (overridden if present in payload), then new ones from payload
      const mergedReviews: ReviewData[] = [
        ...state.reviews
          .map(r => existingReviewsMap.get(r.id))
          .filter((r): r is ReviewData => !!r),
        ...(payload.data ?? []).filter(r => !state.reviews.some(er => er.id === r.id))
      ];
      return {
        reviews: mergedReviews,
        totalElements: payload.total ?? state.totalElements,
        pageToken: payload.nextPageToken,
        loading: false,
        error: undefined
      };
    }),
    on(reviewsApiEvents.loadMoreReviewsFailure, ({ payload: error }) => ({
      loading: false,
      error
    })),
    on(loadReplyTemplates, () => ({
      loadingTemplates: true,
      templateError: undefined
    })),
    on(setReplyFormUi, ({ payload }) => ({
      replyFormActiveTab: payload.activeTab ?? undefined,
      replyFormTemplateMode: payload.templateMode ?? undefined,
      replyFormTemplateSuccessMessage: payload.templateSuccessMessage ?? undefined,
      replyFormLocalTemplateError: payload.localTemplateError ?? undefined,
      replyFormLocalSubmitError: payload.localSubmitError ?? undefined,
      replyFormLocalGenerateCommentError: payload.localGenerateCommentError ?? undefined,
    })),
    on(clearReplyFormLocalErrors, () => ({
      replyFormLocalTemplateError: undefined,
      replyFormLocalSubmitError: undefined,
      replyFormLocalGenerateCommentError: undefined
    })),
    on(reviewsApiEvents.loadReplyTemplatesSuccess, ({ payload }) => ({
      templates: payload.data ?? [],
      loadingTemplates: false,
      templateError: undefined
    })),
    on(reviewsApiEvents.loadReplyTemplatesFailure, ({ payload: templateError }) => ({
      loadingTemplates: false,
      templateError
    })),
    on(createReplyTemplate, () => ({
      savingTemplate: true,
      templateError: undefined
    })),
    on(updateReplyTemplate, () => ({
      savingTemplate: true,
      templateError: undefined
    })),
    on(reviewsApiEvents.createReplyTemplateSuccess, ({ payload }, state) => ({
      savingTemplate: false,
      templateError: undefined,
      templateMutationVersion: state.templateMutationVersion + 1,
      lastMutatedTemplateId: payload.id
    })),
    on(reviewsApiEvents.updateReplyTemplateSuccess, ({ payload }, state) => ({
      savingTemplate: false,
      templateError: undefined,
      templateMutationVersion: state.templateMutationVersion + 1,
      lastMutatedTemplateId: payload.id
    })),
    on(reviewsApiEvents.createReplyTemplateFailure, ({ payload: templateError }) => ({
      savingTemplate: false,
      templateError
    })),
    on(reviewsApiEvents.updateReplyTemplateFailure, ({ payload: templateError }) => ({
      savingTemplate: false,
      templateError
    })),
    on(deleteReplyTemplate, () => ({
      deletingTemplate: true,
      templateError: undefined
    })),
    on(reviewsApiEvents.deleteReplyTemplateSuccess, (_, state) => ({
      deletingTemplate: false,
      templateError: undefined,
      templateMutationVersion: state.templateMutationVersion + 1,
      lastMutatedTemplateId: undefined
    })),
    on(reviewsApiEvents.deleteReplyTemplateFailure, ({ payload: templateError }) => ({
      deletingTemplate: false,
      templateError
    })),
    on(submitReviewReply, () => ({
      submittingReply: true,
      submitError: undefined
    })),
    on(reviewsApiEvents.submitReviewReplySuccess, (_, state) => ({
      submittingReply: false,
      submitError: undefined,
      replySubmitVersion: state.replySubmitVersion + 1
    })),
    on(reviewsApiEvents.submitReviewReplyFailure, ({ payload: submitError }) => ({
      submittingReply: false,
      submitError
    })),
    on(generateComment, () => ({
      generatingComment: true,
      generateCommentError: undefined,
      generatedComment: undefined
    })),
    on(reviewsApiEvents.generateCommentSuccess, ({ payload }) => ({
      generatingComment: false,
      generateCommentError: undefined,
      generatedComment: payload.content
    })),
    on(reviewsApiEvents.generateCommentFailure, ({ payload: generateCommentError }) => ({
      generatingComment: false,
      generateCommentError: "We could not generate a comment right now.",
    })),
    on(loadLabels, () => ({
      loading: true,
      error: undefined,
      labels: [],
      totalElements: 0,
    })),
    on(reviewsApiEvents.listLabelsSuccess, ({ payload }, state) => ({
      labels: [...state.labels, ...(payload ?? [])],
    })),
    // Removed duplicate simple append reducer for loadMoreReviewsSuccess
    on(reviewsApiEvents.listLabelsFailure, ({ payload: error }) => ({
      loading: false,
      error
    })),
    on(tagReviewWithLabel, () => ({
      tagReviewWithLabelError: undefined,
      loadingTagForm: true
    })),
    on(reviewsApiEvents.tagReviewWithLabelSuccess, ({ payload }, state) => {
      const updatedLabels = state.labels.filter(label => payload.labelIds.includes(label.id!));
      return {
        tagReviewWithLabelVersion: state.tagReviewWithLabelVersion + 1,
        loadingTagForm: false,
        reviews: state.reviews.map(review =>
          review.id === payload.reviewId
            ? { ...review, labels: updatedLabels }
            : review
        )
      };
    }),
    on(reviewsApiEvents.tagReviewWithLabelFailure, ({ payload: error }) => ({
      loadingTagForm: false,
      tagReviewWithLabelError: error
    })),
    on(clearReviews, ({ payload: error }) => ({
      generatedComment: undefined,
      generatedCommentError: undefined,
      tagReviewWithLabelError: undefined
    })),
  ),
  withEventHandlers(
    (
      _store,
      events = inject(Events),
      reviewsService = inject(ReviewsService),
      filterStore = inject(FilterStore),
      applicationStore = inject(ApplicationStore)
    ) => ({
      loadReviewsInt$: events
        .on(applicationInitialized, applyFilter, clearFilter, clearAllFilters)
        .pipe(
          filter(() => applicationStore.authenticated()),
          mergeMap(() => {
            const currentFilter = filterStore.filter();
             const request: ReviewsRequest = {
              network: currentFilter.network as Network,
              period: currentFilter.period as Period,
               limit: 10
             };
             return [loadLabels(),loadReviews(request)];
           })
         ),
      loadReviews$: events
        .on(loadReviews)
        .pipe(
          switchMap(({ payload }) => {
            return reviewsService.listReviews(payload).pipe(
              mapResponse({
                next: (response) => reviewsApiEvents.loadReviewsSuccess(response),
                error: () => reviewsApiEvents.loadReviewsFailure('We could not load reviews right now.')
              })
            );
          })
        ),
      loadMoreReviews$: events
        .on(loadMoreReviews)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.listReviews(payload).pipe(
              mapResponse({
                next: (response) => reviewsApiEvents.loadMoreReviewsSuccess(response),
                error: () => reviewsApiEvents.loadMoreReviewsFailure('We could not load more reviews right now.')
              })
            )
          )
        ),
      loadReplyTemplates$: events
        .on(loadReplyTemplates)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.listAssets(payload).pipe(
              mapResponse({
                next: (response) => reviewsApiEvents.loadReplyTemplatesSuccess(response),
                error: () => reviewsApiEvents.loadReplyTemplatesFailure('We could not load reply templates right now.')
              })
            )
          )
        ),
      createReplyTemplate$: events
        .on(createReplyTemplate)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.createAsset(payload).pipe(
              mapResponse({
                next: (asset) => reviewsApiEvents.createReplyTemplateSuccess(asset),
                error: () => reviewsApiEvents.createReplyTemplateFailure('We could not save this template.')
              })
            )
          )
        ),
      updateReplyTemplate$: events
        .on(updateReplyTemplate)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.updateAsset(payload.id, payload.request).pipe(
              mapResponse({
                next: (asset) => reviewsApiEvents.updateReplyTemplateSuccess(asset),
                error: () => reviewsApiEvents.updateReplyTemplateFailure('We could not update this template.')
              })
            )
          )
        ),
      deleteReplyTemplate$: events
        .on(deleteReplyTemplate)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.deleteAsset(payload.id).pipe(
              mapResponse({
                next: () => reviewsApiEvents.deleteReplyTemplateSuccess({ id: payload.id }),
                error: () => reviewsApiEvents.deleteReplyTemplateFailure('We could not delete this template.')
              })
            )
          )
        ),
      submitReviewReply$: events
        .on(submitReviewReply)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.postComment(payload).pipe(
              mapResponse({
                next: () => reviewsApiEvents.submitReviewReplySuccess({ reviewId: payload.reviewId }),
                error: () => reviewsApiEvents.submitReviewReplyFailure('We could not submit your reply.')
              })
            )
          )
        ),
      generateComment$: events
        .on(generateComment)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.generateComment(payload.reviewId).pipe(
              mapResponse({
                next: (response) => reviewsApiEvents.generateCommentSuccess({ content: response.data ?? '' }),
                error: () => reviewsApiEvents.generateCommentFailure('We could not generate a comment right now.')
              })
            )
          )
        ),
      loadLabels$: events
        .on(loadLabels)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.listLabels().pipe(
              mapResponse({
                next: (response) => reviewsApiEvents.listLabelsSuccess(response),
                error: () => reviewsApiEvents.listLabelsFailure('We could not load labels right now.')
              })
            )
          )
        ),
      tagReviewWithLabel$: events
        .on(tagReviewWithLabel)
        .pipe(
          switchMap(({ payload }) =>
            reviewsService.tagReviewWithLabel(payload).pipe(
              mapResponse({
                next: (response) => reviewsApiEvents.tagReviewWithLabelSuccess({ reviewId: payload.reviewId, labelIds: response }),
                error: () => reviewsApiEvents.tagReviewWithLabelFailure('We could not tag the review with labels right now.')
              })
            )
          )
        ),
    }),
  ),
  withHooks({
    onInit(store,
           dispatcher = inject(Dispatcher),
           applicationStore = inject(ApplicationStore),
           filterStore = inject(FilterStore)) {

      if (applicationStore.authenticated()) {
        dispatcher.dispatch(applyFilter(filterStore.filter()));
      }
    }
  })
);

